import React, { useState, useEffect } from 'react'
import { Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useBranding } from '../../context/BrandingContext'
import '../../styles/client-pages.css'

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const IcSend = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
)
const IcPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
)
const IcSearch = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
)
const IcBack = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
)
const IcCheck = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
)
const IcPaperclip = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
  </svg>
)
const IcMsg = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS = {
  pending:     { label: 'Pending',     cls: 'badge-pending' },
  approved:    { label: 'Approved',    cls: 'badge-in_progress' },
  in_progress: { label: 'In Progress', cls: 'badge-in_progress' },
  completed:   { label: 'Done',        cls: 'badge-completed' },
  rejected:    { label: 'Rejected',    cls: 'badge-rejected' },
}

// Progress steps derived from status
function getSteps(status) {
  const steps = [
    { label: 'Submitted',  done: true, active: false },
    { label: 'In Review',  done: ['approved', 'in_progress', 'completed'].includes(status), active: status === 'pending' },
    { label: 'In Progress', done: ['in_progress', 'completed'].includes(status), active: status === 'approved' },
    { label: 'Done',       done: status === 'completed', active: status === 'in_progress' },
  ]
  return steps
}

// ── Request list item ─────────────────────────────────────────────────────────
function RequestListItem({ req, selected, onClick }) {
  const b = STATUS[req.status] || STATUS.pending
  const preview = req.description?.slice(0, 80) || ''
  return (
    <div className={`request-list-item${selected ? ' selected' : ''}`} onClick={onClick}>
      <div className="request-list-meta">
        <span className={`request-badge ${b.cls}`}>{b.label}</span>
        <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'monospace' }}>
          {req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}
        </span>
      </div>
      <div className="request-list-title">{req.title}</div>
      {preview && <div className="request-list-preview">{preview}</div>}
    </div>
  )
}

// ── Thread panel ──────────────────────────────────────────────────────────────
function ThreadPanel({ request }) {
  const { cmsName } = useBranding()
  const queryClient = useQueryClient()
  const [reply, setReply] = useState('')
  const steps = getSteps(request?.status)

  const replyMutation = useMutation(
    (msg) => apiService.post(`/requests/${request.id}/replies`, { message: msg }),
    {
      onSuccess: () => {
        toast.success('Reply sent!')
        queryClient.invalidateQueries(['edit-requests'])
        setReply('')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to send reply')
    }
  )

  if (!request) {
    return (
      <div className="requests-thread-panel">
        <div className="thread-empty">
          <IcMsg />
          <div style={{ fontSize: 14, fontWeight: 500 }}>No request selected</div>
          <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>Select a request from the list to view the conversation</div>
        </div>
      </div>
    )
  }

  const b = STATUS[request.status] || STATUS.pending

  return (
    <div className="requests-thread-panel">
      {/* Header */}
      <div className="thread-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span className={`request-badge ${b.cls}`}>{b.label}</span>
          {request.category && (
            <span className="request-badge" style={{ background: 'var(--border)', color: 'var(--text-muted)' }}>{request.category}</span>
          )}
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{request.title}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 4, fontFamily: 'monospace' }}>
          Opened {request.created_at ? new Date(request.created_at).toLocaleString() : ''}
          {request.site_name ? ` · ${request.site_name}` : ''}
        </div>
      </div>

      {/* Progress steps */}
      <div className="progress-steps">
        {steps.map((step, i) => (
          <React.Fragment key={i}>
            <div className={`progress-step${step.active ? ' active' : step.done ? ' done' : ''}`}>
              <div className="progress-dot" />
              <div style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.3, maxWidth: 80 }}>{step.label}</div>
              {i < steps.length - 1 && <div className="progress-connector" />}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Messages */}
      <div className="thread-messages">
        {/* Original description as first message */}
        {request.description && (
          <div className="thread-msg mine">
            <div className="thread-avatar">
              {(request.client_name || 'You')[0].toUpperCase()}
            </div>
            <div>
              <div className="thread-bubble">{request.description}</div>
              <div className="thread-time" style={{ textAlign: 'right' }}>
                {request.created_at ? new Date(request.created_at).toLocaleString() : ''}
              </div>
            </div>
          </div>
        )}
        {/* Admin notes */}
        {request.approval_notes && (
          <div className="thread-msg">
            <div className="thread-avatar admin">T</div>
            <div>
              <div className="thread-bubble" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }}>
                {request.approval_notes}
              </div>
              <div className="thread-time">{cmsName} team</div>
            </div>
          </div>
        )}
        {request.completion_notes && (
          <div className="thread-msg">
            <div className="thread-avatar admin">T</div>
            <div>
              <div className="thread-bubble" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }}>
                {request.completion_notes}
              </div>
              <div className="thread-time">{cmsName} team · {request.completed_at ? new Date(request.completed_at).toLocaleString() : ''}</div>
            </div>
          </div>
        )}
        {request.rejection_reason && (
          <div className="thread-msg">
            <div className="thread-avatar" style={{ background: '#ef4444' }}>T</div>
            <div>
              <div className="thread-bubble" style={{ background: 'var(--surface)', borderColor: 'var(--border)', color: 'var(--text)' }}>
                {request.rejection_reason}
              </div>
              <div className="thread-time">{cmsName} team</div>
            </div>
          </div>
        )}
      </div>

      {/* Reply composer */}
      {request.status !== 'completed' && request.status !== 'rejected' && (
        <div className="thread-composer">
          <textarea
            placeholder="Type your reply…"
            value={reply}
            onChange={e => setReply(e.target.value)}
            rows={2}
          />
          <button
            className="ds-btn ds-btn-primary"
            style={{ alignSelf: 'flex-end' }}
            disabled={!reply.trim() || replyMutation.isLoading}
            onClick={() => reply.trim() && replyMutation.mutate(reply.trim())}
          >
            <IcSend /> {replyMutation.isLoading ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── List view ─────────────────────────────────────────────────────────────────
const ClientRequestsList = () => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch]             = useState('')
  const [selectedId, setSelectedId]     = useState(null)
  const [searchParams]                  = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedId(openId)
  }, [searchParams])

  // Preserved API call
  const { data: requests = [], isLoading } = useQuery(
    ['edit-requests', statusFilter],
    () => apiService.get('/requests', {
      params: { status: statusFilter !== 'all' ? statusFilter : undefined }
    }).then(res => res.data)
  )

  const filtered = requests.filter(r =>
    r.title?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  )
  const selected = filtered.find(r => String(r.id) === String(selectedId)) || null

  const filters = ['all', 'pending', 'approved', 'in_progress', 'completed']

  return (
    <div>
      {/* Page header */}
      <div className="page-header" style={{ marginBottom: 16 }}>
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, color: 'var(--text)' }}>Requests</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Ask your team to make any change — typical first reply in under 4 hours.
          </div>
        </div>
        <Link to="/client/requests/new" style={{ textDecoration: 'none' }}>
          <button className="ds-btn ds-btn-primary"><IcPlus /> New request</button>
        </Link>
      </div>

      {/* Split panel */}
      <div className="requests-split">
        {/* Left list panel */}
        <div className="requests-list-panel">
          <div className="requests-list-filter">
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}>
                <IcSearch />
              </span>
              <input
                placeholder="Search requests…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', paddingLeft: 32, paddingRight: 10, paddingTop: 7, paddingBottom: 7, background: 'var(--bg-0)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12.5, color: 'var(--text)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
            </div>
            {/* Filter buttons */}
            <div className="requests-filter-row">
              {filters.map(f => (
                <button key={f} className={`requests-filter-btn${statusFilter === f ? ' active' : ''}`} onClick={() => setStatusFilter(f)}>
                  {f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="requests-list-items">
            {isLoading ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                {search || statusFilter !== 'all' ? 'No matching requests' : 'No requests yet'}
              </div>
            ) : (
              filtered.map(req => (
                <RequestListItem
                  key={req.id}
                  req={req}
                  selected={String(req.id) === String(selectedId)}
                  onClick={() => setSelectedId(String(req.id))}
                />
              ))
            )}
          </div>
        </div>

        {/* Right thread panel */}
        <ThreadPanel request={selected} />
      </div>
    </div>
  )
}

// ── New request form ──────────────────────────────────────────────────────────
const NewEditRequest = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({ site_id: '', title: '', category: '', priority: 'medium', description: '', page_url: '' })
  const [attachments, setAttachments] = useState([])

  // Preserved API calls
  const { data: sites = [] } = useQuery(
    'client-sites',
    () => apiService.get('/sites').then(res => res.data.sites || [])
  )

  const createRequestMutation = useMutation(
    (data) => {
      const fd = new FormData()
      fd.append('site_id', data.site_id)
      fd.append('title', data.title)
      fd.append('category', data.category)
      fd.append('priority', data.priority)
      fd.append('description', data.description)
      if (data.page_url) fd.append('page_url', data.page_url)
      if (attachments[0]) fd.append('screenshot', attachments[0])
      return apiService.post('/requests', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
    },
    {
      onSuccess: () => {
        toast.success('Edit request submitted successfully!')
        queryClient.invalidateQueries('edit-requests')
        navigate('/client/requests')
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to submit edit request')
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.site_id || !formData.title.trim() || !formData.category || !formData.description.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    createRequestMutation.mutate(formData)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: 'var(--text)' }}>New Edit Request</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Submit a request for changes to your website</div>
        </div>
        <Link to="/client/requests" style={{ textDecoration: 'none' }}>
          <button className="ds-btn ds-btn-ghost"><IcBack /> Back</button>
        </Link>
      </div>

      <div className="card card-pad" style={{ maxWidth: 640 }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Site */}
          <div className="help-field">
            <label>Select Site *</label>
            <select value={formData.site_id} onChange={e => setFormData(p => ({ ...p, site_id: e.target.value }))} required>
              <option value="">Choose a site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {/* Title */}
          <div className="help-field">
            <label>Request Title *</label>
            <input type="text" value={formData.title} onChange={e => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="Briefly describe what you need" required />
          </div>

          {/* Category */}
          <div className="help-field">
            <label>Category *</label>
            <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))} required>
              <option value="">Select category</option>
              <option value="content">Content Update</option>
              <option value="design">Design Change</option>
              <option value="functionality">New Functionality</option>
              <option value="bug">Bug Fix</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Priority */}
          <div className="help-field">
            <label>Priority</label>
            <select value={formData.priority} onChange={e => setFormData(p => ({ ...p, priority: e.target.value }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* Page URL */}
          <div className="help-field">
            <label>Page URL (Optional)</label>
            <input type="url" value={formData.page_url} onChange={e => setFormData(p => ({ ...p, page_url: e.target.value }))} placeholder="https://example.com/page" />
          </div>

          {/* Description */}
          <div className="help-field">
            <label>Description *</label>
            <textarea rows={5} value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Please provide detailed information about your request…" required style={{ minHeight: 100 }} />
          </div>

          {/* Screenshot */}
          <div className="help-field">
            <label>Screenshot (Optional)</label>
            <div style={{ border: '2px dashed var(--border)', borderRadius: 10, padding: '20px', textAlign: 'center' }}>
              <input type="file" id="screenshot-upload" accept="image/*" onChange={e => setAttachments(Array.from(e.target.files))} style={{ display: 'none' }} />
              <label htmlFor="screenshot-upload" style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>
                <IcPaperclip />
                <div style={{ marginTop: 6 }}>Click to upload a screenshot</div>
                <div style={{ fontSize: 11.5, marginTop: 3 }}>Images up to 10MB</div>
              </label>
              {attachments.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12.5, color: '#22c55e' }}>{attachments[0].name}</div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="ds-btn ds-btn-primary" disabled={createRequestMutation.isLoading}>
              <IcSend /> {createRequestMutation.isLoading ? 'Submitting…' : 'Submit Request'}
            </button>
            <Link to="/client/requests" style={{ textDecoration: 'none' }}>
              <button type="button" className="ds-btn ds-btn-ghost">Cancel</button>
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Router wrapper ────────────────────────────────────────────────────────────
const ClientRequests = () => (
  <Routes>
    <Route index element={<ClientRequestsList />} />
    <Route path="new" element={<NewEditRequest />} />
  </Routes>
)

export default ClientRequests
